import { Document, Packer, Paragraph, TextRun } from 'docx';

// Format configurations
const DEFAULT_FORMATS = {
    heading1: {
        prefix: '# ',
        style: { size: 36, bold: true },
    },
    heading2: {
        prefix: '## ',
        style: { size: 32, bold: true },
    },
    bold: {
        pattern: /\*\*.*?\*\*/g,
        wrapper: '**',
        style: { size: 24, bold: true },
    },
    default: {
        style: { size: 24 },
    },
};

const createTextRun = (text, style = {}) => {
    return new TextRun({
        text,
        ...style,
    });
};

const formatParagraph = (text, formats = DEFAULT_FORMATS) => {
    // Check for headings
    for (const format of Object.values(formats)) {
        if (format.prefix && text.startsWith(format.prefix)) {
            return new Paragraph({
                children: [createTextRun(text.replace(format.prefix, ''), format.style)],
            });
        }
    }

    // Handle inline formatting (like bold)
    const inlineFormats = Object.values(formats).filter(f => f.pattern);
    let parts = [text];

    inlineFormats.forEach(format => {
        parts = parts.flatMap(part => {
            if (typeof part === 'string') {
                return part.split(format.pattern);
            }
            return [part];
        });
    });

    return new Paragraph({
        children: parts.map(part => {
            for (const format of inlineFormats) {
                if (
                    typeof part === 'string' &&
                    part.startsWith(format.wrapper) &&
                    part.endsWith(format.wrapper)
                ) {
                    return createTextRun(
                        part.slice(format.wrapper.length, -format.wrapper.length),
                        format.style,
                    );
                }
            }
            return createTextRun(part, formats.default.style);
        }),
    });
};

export const formatText = (text, formats = DEFAULT_FORMATS) => {
    const paragraphs = text.split('\n');
    return paragraphs.map(paragraph => formatParagraph(paragraph, formats));
};

export const generateDocx = async (
    docContent,
    fileName = 'job-description',
    customFormats = DEFAULT_FORMATS,
) => {
    const doc = new Document({
        sections: [
            {
                properties: {},
                children: formatText(docContent, customFormats),
            },
        ],
    });

    // Generate blob
    const blob = await Packer.toBlob(doc);

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.docx`;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const formatJobDescription = jd => {
    if (!jd) return '';
    return jd
        .replace(/##\s*(.*)/g, '<b>$1</b>') // Convert ## to bold
        .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>') // Convert ** to bold
        .replace(/#\s*(.*)/g, '<b>$1</b>') // Convert # to bold
        .replace(/\*\s*(.*)/g, '<br>$1') // Convert * to line break and add text
        .replace(/\n{2,}/g, '<br><br>') // Reduce consecutive new lines to one line break
        .replace(/\n/g, '<br>'); // Convert remaining new lines to <br>
};
